import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import MainLayout from '../../layouts/MainLayout';
import { Card, Button, Space, Tabs, Tag, Avatar, message, Popconfirm, Select, Input, InputNumber, Spin, Slider, Modal } from 'antd';
import { 
  EditOutlined, 
  ArrowRightOutlined, 
  DollarOutlined,
  DeleteOutlined,
  CalendarOutlined,
  FileTextOutlined,
  HistoryOutlined,
  PhoneOutlined,
  CheckCircleOutlined,
  SwapOutlined,
  FlagOutlined,
  TagOutlined,
  FileOutlined,
  UserOutlined,
  ShoppingOutlined,
  PartitionOutlined,
  CopyOutlined,
  MessageOutlined,
} from '@ant-design/icons';
import { dealsApi, leadSettingsApi } from '../../utils/api';
import { resolveAvatarSrc } from '../../utils/avatarUtils';
import { DetailPageSkeleton } from '../../components/SkeletonLoaders';
import InlineEditActions from '../../components/common/InlineEditActions';
import PriorityStars from '../../components/common/PriorityStars';
import EntityMiniStatCards from '../../components/common/EntityMiniStatCards';
import { useClickOutsideWhen } from '../../hooks/useClickOutsideWhen';
import FollowUpTab from '../../components/deals/ViewDealTabs/FollowUpTab';
import FilesTab from '../../components/deals/ViewDealTabs/FilesTab';
import NotesTab from '../../components/deals/ViewDealTabs/NotesTab';
import HistoryTab from '../../components/deals/ViewDealTabs/HistoryTab';
import BillingTab from '../../components/deals/ViewDealTabs/BillingTab';
import SmsHistoryPanel from '../../components/common/SmsHistoryPanel';
import PersianDatePicker from '../../components/PersianDatePicker';
import moment from 'moment-jalaali';
import { useDetailReturnRefs } from '../../hooks/useListReturnState';
import { useEntityBreadcrumb } from '../../hooks/useEntityBreadcrumb';
import EntityBreadcrumb from '../../components/common/EntityBreadcrumb';
import { applyDealFieldUpdate } from '../../utils/inlineFieldUpdateMerge';
import { formatPriceToman } from '../../utils/formatPriceToman';
import { formatJalaliApiDate, toJalali, formatCloseDateLabel } from '../../utils/dateUtils';
import { resolveSourceId, resolveSourceName as getSourceName, resolveCategoryName as getCategoryName, resolveDetailFormFilters } from '../../utils/sourceUtils';
import TabNotesLabel from '../../components/common/TabNotesLabel';
import CopyIconButton from '../../components/common/CopyIconButton';
import SendSmsIconButton from '../../components/common/SendSmsIconButton';
import ClickToCallButton from '../../components/call-center/ClickToCallButton';
import SendSmsModal from '../../components/common/SendSmsModal';
import DealRelatedContactPanel from '../../components/deals/DealRelatedContactPanel';
import ExpandableText from '../../components/common/ExpandableText';
import RejectReasonFields from '../../components/common/RejectReasonFields';
import { buildRejectPayload, validateRejectSelection } from '../../utils/rejectReasonUtils';
import { isDealLostStage } from '../../utils/dealStageUtils';
import { buildDealConsultantPrompt } from '../../utils/consultantPromptUtils';
import { canDeleteLeadRecord, canManageLeadRecord } from '../../utils/leadRecordPermissions';

const SUCCESS_RATE_SLIDER_MARKS = { 0: '0', 25: '25', 50: '50', 75: '75', 100: '100' };

const ViewDeal = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { returnToRef, buildGoBackState } = useDetailReturnRefs();
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const DEAL_TABS = ['follow-up', 'files', 'notes', 'history', 'billing', 'sms-history'];
  const [loading, setLoading] = useState(true);
  const [tabLoading, setTabLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    const tabFromUrl = searchParams.get('tab');
    return DEAL_TABS.includes(tabFromUrl) ? tabFromUrl : 'follow-up';
  });
  const skipNextTabFetchRef = useRef(true);
  const tabCacheRef = useRef({});
  const [deal, setDeal] = useState(null);
  const [lead, setLead] = useState(null);
  const [customer, setCustomer] = useState(null);
  const { trail, navigateWithTrail, goToCrumb } = useEntityBreadcrumb({
    listPath: '/leads/deals',
    listLabel: 'معاملات',
    entityLabel: deal?.name || (id ? `معامله #${id}` : 'معامله'),
  });
  const [tabData, setTabData] = useState(null);
  const [filters, setFilters] = useState(null);
  const [customFields, setCustomFields] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [changingStage, setChangingStage] = useState(false);
  const [pipelineStages, setPipelineStages] = useState([]);
  const [allPipelines, setAllPipelines] = useState([]);
  const [dealPipelines, setDealPipelines] = useState([]);
  const [editingField, setEditingField] = useState(null);
  const [fieldValue, setFieldValue] = useState(null);
  const [updatingField, setUpdatingField] = useState(false);
  const [notesCount, setNotesCount] = useState(0);
  const [smsModalOpen, setSmsModalOpen] = useState(false);
  const [copyingPrompt, setCopyingPrompt] = useState(false);
  const [rejectStageModal, setRejectStageModal] = useState({
    open: false,
    stageId: null,
    pipelineId: null,
    mode: 'stage',
    reasonId: null,
    reasonText: '',
  });
  const [rejectReasonsFallback, setRejectReasonsFallback] = useState([]);
  const editingBoxRef = useRef(null);
  const getFieldBoxRef = (field) => (editingField === field ? editingBoxRef : null);

  const resolveDealPipelineId = (d) =>
    d?.pipeline?.id ?? d?.pipeline_id ?? d?.lead_pipeline_id ?? null;

  const pipelineOptions = useMemo(() => {
    const source = dealPipelines.length > 0 ? dealPipelines : allPipelines;
    return source.map((p) => ({
      value: Number(p.id),
      label: p.name,
    }));
  }, [dealPipelines, allPipelines]);

  const getStagesForPipeline = (pipelineId) => {
    if (!pipelineId) return [];
    const pipeline = allPipelines.find((p) => Number(p.id) === Number(pipelineId));
    return (pipeline?.stages || [])
      .map((s) => ({
        id: Number(s.id),
        name: s.name,
        label_color: s.label_color || s.color || '#1890ff',
        priority: s.priority || 0,
      }))
      .sort((a, b) => (a.priority || 0) - (b.priority || 0));
  };

  const pipelineEditStages = useMemo(() => {
    if (editingField !== 'pipeline' || !fieldValue?.pipelineId) return [];
    return getStagesForPipeline(fieldValue.pipelineId);
  }, [editingField, fieldValue?.pipelineId, allPipelines]);

  const resolveSourceName = (record) => getSourceName(record, filters?.sources);
  const resolveCategoryName = (record) => getCategoryName(record, filters?.categories);

  // دریافت تمام پایپلاین‌ها هنگام بارگذاری صفحه
  useEffect(() => {
    const fetchPipelines = async () => {
      try {
        const pipelinesRes = await leadSettingsApi.getPipelines();
        const pipelines = pipelinesRes?.data?.pipelines || pipelinesRes?.pipelines || [];
        setAllPipelines(pipelines);
      } catch (err) {
        console.warn('Could not fetch pipelines:', err);
      }
    };
    fetchPipelines();
  }, []);

  const applyDealResponse = (res, { updateDeal = true } = {}) => {
    const dealData = res?.deal || null;
    if (updateDeal) {
      setDeal(dealData);
      setLead(res?.lead || null);
      setCustomer(res?.customer || null);
      setCustomFields(res?.custom_fields || null);
      setPermissions(res?.permissions || null);
      if (Array.isArray(res?.pipelines) && res.pipelines.length > 0) {
        setDealPipelines(res.pipelines);
      }
      if (dealData?.notes_count != null) {
        setNotesCount(Number(dealData.notes_count) || 0);
      }
      const responseStages = res?.stages || res?.filters?.stages || res?.pipeline_stages || [];
      if (responseStages.length > 0) {
        const stages = responseStages
          .map((s) => ({
            id: Number(s.id ?? s.value),
            name: s.name ?? s.label,
            label_color: s.label_color || s.color || s.labelColor || '#1890ff',
            priority: s.priority || 0,
          }))
          .sort((a, b) => (a.priority || 0) - (b.priority || 0));
        setPipelineStages(stages);
      }
    }
    return res?.tab_data ?? [];
  };

  const resolveNotesCountFromTabData = (tabData) => {
    if (!tabData) return 0;
    if (Array.isArray(tabData?.notes)) return tabData.notes.length;
    if (Array.isArray(tabData)) return tabData.length;
    return 0;
  };

  useEffect(() => {
    if (!id) return;
    tabCacheRef.current = {};
    const loadDeal = async () => {
      setLoading(true);
      skipNextTabFetchRef.current = true;
      try {
        const bootTab = activeTab === 'sms-history' ? 'follow-up' : activeTab;
        const res = await dealsApi.getById(id, bootTab);
        const nextTabData = applyDealResponse(res);
        if (activeTab !== 'sms-history') {
          setTabData(nextTabData);
          setFilters(resolveDetailFormFilters(res));
          if (activeTab === 'notes') {
            setNotesCount(resolveNotesCountFromTabData(nextTabData));
          }
          tabCacheRef.current[activeTab] = {
            tabData: nextTabData,
            filters: resolveDetailFormFilters(res),
          };
        } else {
          tabCacheRef.current[bootTab] = {
            tabData: nextTabData,
            filters: resolveDetailFormFilters(res),
          };
        }
      } catch (error) {
        console.error('Error fetching deal:', error);
        message.error('خطا در دریافت اطلاعات معامله');
      } finally {
        setLoading(false);
      }
    };
    loadDeal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl && DEAL_TABS.includes(tabFromUrl) && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    const prefetchNotesCount = async () => {
      try {
        const res = await dealsApi.getById(id, 'notes');
        if (cancelled) return;
        const notesData = res?.tab_data ?? [];
        const count = resolveNotesCountFromTabData(notesData);
        if (count > 0) {
          setNotesCount(count);
        } else if (res?.deal?.notes_count != null) {
          setNotesCount(Number(res.deal.notes_count) || 0);
        } else {
          setNotesCount(0);
        }
        tabCacheRef.current.notes = {
          tabData: notesData,
          filters: resolveDetailFormFilters(res),
        };
      } catch (error) {
        console.warn('Could not prefetch deal notes count:', error);
      }
    };
    prefetchNotesCount();
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    if (!id || loading) return;
    if (activeTab === 'sms-history') return;
    if (skipNextTabFetchRef.current) {
      skipNextTabFetchRef.current = false;
      return;
    }
    const cached = tabCacheRef.current[activeTab];
    if (cached) {
      setTabData(cached.tabData);
      if (cached.filters) setFilters(cached.filters);
      return;
    }
    const loadTab = async () => {
      setTabLoading(true);
      try {
        const res = await dealsApi.getById(id, activeTab);
        const nextTabData = applyDealResponse(res, { updateDeal: false });
        setTabData(nextTabData);
        if (res) setFilters(resolveDetailFormFilters(res));
        if (activeTab === 'notes') {
          setNotesCount(resolveNotesCountFromTabData(nextTabData));
        }
        tabCacheRef.current[activeTab] = {
          tabData: nextTabData,
          filters: resolveDetailFormFilters(res),
        };
      } catch (error) {
        console.error('Error fetching tab:', error);
        message.error('خطا در دریافت اطلاعات تب');
      } finally {
        setTabLoading(false);
      }
    };
    loadTab();
  }, [activeTab, id, loading]);

  // تنظیم مراحل بر اساس پایپلاین معامله - fallback در صورت عدم وجود stages در پاسخ API
  useEffect(() => {
    // اگر stages از API تنظیم شده، نیازی به fallback نیست
    if (pipelineStages.length > 0 || !deal) return;
    
    const dealStageId = deal.stage_id || deal.pipeline_stage_id;
    
    // اگر هنوز مراحل پیدا نشد، مستقیماً از API مرحله واکشی کن (fallback)
    if (dealStageId) {
      const fetchStagesFromStageApi = async () => {
        try {
          const res = await leadSettingsApi.getLeadStage(dealStageId);
          const responseData = res?.data || res;
          const stages = responseData?.data?.pipeline_stages || responseData?.pipeline_stages || [];
          if (stages.length > 0) {
            const mappedStages = stages
              .map((s) => ({
                id: Number(s.id ?? s.value),
                name: s.name ?? s.label,
                label_color: s.label_color || s.color || s.labelColor || '#1890ff',
                priority: s.priority || 0,
              }))
              .sort((a, b) => (a.priority || 0) - (b.priority || 0));
            setPipelineStages(mappedStages);
          }
        } catch (err) {
          console.warn('Could not fetch pipeline stages from stage API:', err);
        }
      };
      fetchStagesFromStageApi();
    }
  }, [deal, pipelineStages.length]);

  const fetchDealData = async (tab = activeTab) => {
    setTabLoading(true);
    try {
      const res = await dealsApi.getById(id, tab);
      const nextTabData = applyDealResponse(res);
      setTabData(nextTabData);
      setFilters(resolveDetailFormFilters(res));
      if (tab === 'notes') {
        setNotesCount(resolveNotesCountFromTabData(nextTabData));
      }
      tabCacheRef.current[tab] = {
        tabData: nextTabData,
        filters: resolveDetailFormFilters(res),
      };
    } catch (error) {
      console.error('Error fetching deal:', error);
      message.error('خطا در دریافت اطلاعات معامله');
    } finally {
      setTabLoading(false);
    }
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', key);
        return next;
      },
      { replace: true, state: location.state || {} },
    );
  };

  const goBackToList = () => {
    navigate(returnToRef.current || '/leads/deals', {
      state: buildGoBackState(),
    });
  };

  const handleDelete = async () => {
    try {
      await dealsApi.delete(id);
      message.success('معامله با موفقیت حذف شد');
      goBackToList();
    } catch (error) {
      console.error('Error deleting deal:', error);
      message.error('خطا در حذف معامله');
    }
  };

  const applyStageChange = async (stageId, rejectPayload) => {
    const stageIdNum = Number(stageId);
    const currentStageId = Number(deal?.pipeline_stage_id || deal?.stage_id);
    if (!stageIdNum || stageIdNum === currentStageId) return;
    
    const previousStageId = currentStageId;
    
    // به‌روزرسانی موقت UI
    setDeal(prev => ({ ...prev, stage_id: stageIdNum, pipeline_stage_id: stageIdNum }));
    setChangingStage(true);
    
    try {
      await dealsApi.changeStage(id, stageIdNum, rejectPayload);
      message.success('مرحله معامله با موفقیت تغییر کرد');
      await fetchDealData(activeTab);
    } catch (error) {
      console.error('Error changing stage:', error);
      // بازگردانی به مرحله قبلی در صورت خطا
      setDeal(prev => ({ ...prev, stage_id: previousStageId, pipeline_stage_id: previousStageId }));
      message.error('خطا در تغییر مرحله معامله');
    } finally {
      setChangingStage(false);
    }
  };

  const applyPipelineChange = async (pipelineId, stageId, rejectPayload) => {
    const pipelineIdNum = Number(pipelineId);
    const stageIdNum = Number(stageId);
    const currentPipelineId = Number(resolveDealPipelineId(deal));
    const currentStageId = Number(deal?.pipeline_stage_id || deal?.stage_id);
    if (!pipelineIdNum || !stageIdNum) return;
    if (pipelineIdNum === currentPipelineId && stageIdNum === currentStageId) {
      setEditingField(null);
      setFieldValue(null);
      return;
    }

    setChangingStage(true);
    try {
      await dealsApi.changePipeline(id, pipelineIdNum, stageIdNum, rejectPayload);
      message.success('مسیر و مرحله معامله با موفقیت تغییر کرد');
      setEditingField(null);
      setFieldValue(null);
      await fetchDealData(activeTab);
    } catch (error) {
      console.error('Error changing pipeline:', error);
      message.error('خطا در تغییر مسیر فروش');
    } finally {
      setChangingStage(false);
    }
  };

  const openRejectStageModal = async ({ stageId, pipelineId = null, mode = 'stage' }) => {
    setRejectStageModal({
      open: true,
      stageId,
      pipelineId,
      mode,
      reasonId: null,
      reasonText: '',
    });
    if (filters?.reject_reasons?.length) return;
    try {
      const res = await leadSettingsApi.getSettings('reject-reason');
      const data = res?.data?.data || res?.data || res || {};
      setRejectReasonsFallback(data.reject_reasons || []);
    } catch (error) {
      console.error('Error loading reject reasons:', error);
    }
  };

  const handleStageChange = async (stageId) => {
    const targetStage = pipelineStages.find((stage) => Number(stage.id) === Number(stageId));
    if (isDealLostStage(targetStage)) {
      await openRejectStageModal({ stageId, pipelineId: null, mode: 'stage' });
      return;
    }
    await applyStageChange(stageId);
  };

  const handleSavePipelineEdit = async () => {
    const pipelineId = fieldValue?.pipelineId;
    const stageId = fieldValue?.stageId;
    if (pipelineId == null || pipelineId === '') {
      message.warning('لطفاً مسیر فروش را انتخاب کنید');
      return;
    }
    if (stageId == null || stageId === '') {
      message.warning('لطفاً مرحله فروش را انتخاب کنید');
      return;
    }
    const targetStage = getStagesForPipeline(pipelineId).find(
      (stage) => Number(stage.id) === Number(stageId),
    );
    if (isDealLostStage(targetStage)) {
      await openRejectStageModal({ stageId, pipelineId, mode: 'pipeline' });
      return;
    }
    await applyPipelineChange(pipelineId, stageId);
  };

  const startEditingPipeline = () => {
    setEditingField('pipeline');
    setFieldValue({
      pipelineId: resolveDealPipelineId(deal) ? Number(resolveDealPipelineId(deal)) : undefined,
      stageId: deal?.pipeline_stage_id || deal?.stage_id
        ? Number(deal.pipeline_stage_id || deal.stage_id)
        : undefined,
    });
  };

  const handleConfirmRejectStage = async () => {
    const reasons = filters?.reject_reasons?.length
      ? filters.reject_reasons
      : rejectReasonsFallback;
    const validationError = validateRejectSelection({
      reasonId: rejectStageModal.reasonId,
      reasonText: rejectStageModal.reasonText,
      reasons,
    });
    if (validationError) {
      message.warning(validationError);
      return;
    }
    const payload = buildRejectPayload({
      reasonId: rejectStageModal.reasonId,
      reasonText: rejectStageModal.reasonText,
      reasons,
    });
    const { stageId, pipelineId, mode } = rejectStageModal;
    setRejectStageModal({
      open: false,
      stageId: null,
      pipelineId: null,
      mode: 'stage',
      reasonId: null,
      reasonText: '',
    });
    if (mode === 'pipeline') {
      await applyPipelineChange(pipelineId, stageId, payload);
    } else {
      await applyStageChange(stageId, payload);
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      1: 'red',
      2: 'orange',
      3: 'blue',
      '1': 'red',
      '2': 'orange',
      '3': 'blue',
    };
    return colors[priority] || 'default';
  };

  const getPriorityLabel = (priority) => {
    const labels = {
      1: 'بالا',
      2: 'متوسط',
      3: 'پایین',
      '1': 'بالا',
      '2': 'متوسط',
      '3': 'پایین',
    };
    return labels[priority] || 'نامشخص';
  };

  const handleUpdateField = async (field, value) => {
    // مقایسه ویژه برای فیلدهای آرایه‌ای (تگ‌ها، محصولات)
    const arrayFields = ['tags', 'product_id'];
    if (arrayFields.includes(field)) {
      const currentIds = (deal[field] || deal.tags || deal.products || []).map(t => t.id || t).sort().join(',');
      const newIds = (Array.isArray(value) ? value : []).sort().join(',');
      if (currentIds === newIds) {
        setEditingField(null);
        setFieldValue(null);
        return;
      }
    } else if (field === 'close_date') {
      const currentDate = formatJalaliApiDate(deal?.close_date);
      const newDate = formatJalaliApiDate(value);
      if (currentDate === newDate) {
        setEditingField(null);
        setFieldValue(null);
        return;
      }
    } else if (field === 'source_id') {
      if (String(value ?? '') === String(resolveSourceId(deal) ?? '')) {
        setEditingField(null);
        setFieldValue(null);
        return;
      }
    } else if (value === deal?.[field] || (value === null && !deal?.[field])) {
      setEditingField(null);
      setFieldValue(null);
      return;
    }

    setUpdatingField(true);
    const previousDeal = deal;

    try {
      const payloadValue = field === 'close_date' ? formatJalaliApiDate(value) : value;
      const res = await dealsApi.updateSingleField(id, field, Array.isArray(payloadValue) ? payloadValue : payloadValue);
      const updatedDeal =
        res?.deal ??
        res?.data?.deal ??
        (res?.data && typeof res.data === 'object' && !Array.isArray(res.data) ? res.data : null);
      setDeal((prev) =>
        applyDealFieldUpdate(prev, field, payloadValue, updatedDeal || res, filters || {}),
      );
      if (field === 'source_id') {
        message.success('شیوه آشنایی برای مشتری و سایر معاملات این مشتری نیز به‌روزرسانی شد');
      } else {
        message.success('فیلد با موفقیت به‌روزرسانی شد');
      }
    } catch (error) {
      console.error('Error updating field:', error);
      setDeal(previousDeal);
      message.error('خطا در به‌روزرسانی فیلد');
    } finally {
      setUpdatingField(false);
      setEditingField(null);
      setFieldValue(null);
    }
  };

  const handleCopyPrompt = async () => {
    if (!deal) return;
    setCopyingPrompt(true);
    try {
      const clientName = customer?.name || lead?.client_name || lead?.name || '';
      const prompt = buildDealConsultantPrompt({ deal, clientName });
      await navigator.clipboard.writeText(prompt);
      message.success('پرامپت سناریوی تماس کپی شد');
    } catch (error) {
      console.error('copy prompt failed', error);
      message.error('خطا در کپی؛ لطفاً دوباره تلاش کنید');
    } finally {
      setCopyingPrompt(false);
    }
  };

  const startEditing = (field, currentValue) => {
    // اگر توضیحات در حال ویرایش و ذخیره‌نشده است، با رفتن به فیلد دیگر پاک نشود
    if (editingField === 'note' && field !== 'note') {
      const original = deal?.note ?? '';
      const current = fieldValue ?? '';
      if (String(current) !== String(original)) {
        Modal.confirm({
          title: 'توضیحات ذخیره نشده',
          content: 'ابتدا توضیحات را ذخیره کنید یا تغییرات را لغو کنید، سپس فیلد دیگر را ویرایش کنید.',
          okText: 'ذخیره توضیحات',
          cancelText: 'ادامه ویرایش توضیحات',
          onOk: () => handleUpdateField('note', fieldValue),
        });
        return;
      }
    }

    setEditingField(field);
    if (field === 'deal_owner') {
      setFieldValue(resolveDealOwnerId(deal));
      return;
    }
    if (field === 'close_date') {
      setFieldValue(currentValue ? moment(currentValue).toDate() : null);
      return;
    }
    if (field === 'priority' && currentValue != null && currentValue !== '') {
      setFieldValue(String(currentValue));
      return;
    }
    setFieldValue(currentValue);
  };


  const cancelEditing = () => {
    setEditingField(null);
    setFieldValue(null);
  };

  // برای توضیحات: کلیک بیرون نباید پیش‌نویس را دور بیندازد (مثلاً هنگام تغییر مرحله)
  useClickOutsideWhen(
    Boolean(editingField) && editingField !== 'note',
    cancelEditing,
    editingBoxRef,
  );

  const resolveDealOwnerId = (d) => {
    const owner = d?.deal_owner;
    if (owner != null && typeof owner === 'object') return owner.id;
    if (owner != null) return owner;
    return d?.owner?.id ?? null;
  };

  const resolveDealOwnerName = (d) => d?.deal_owner?.name ?? d?.owner?.name ?? null;

  const tagOptions = useMemo(() => {
    const map = new Map();
    (filters?.tags || []).forEach((tag) => map.set(tag.id, tag));
    (deal?.tags || []).forEach((tag) => map.set(tag.id, tag));
    return Array.from(map.values());
  }, [filters?.tags, deal?.tags]);

  if (loading || !deal) {
    return (
      <MainLayout>
        <div className="p-6">
          <DetailPageSkeleton />
        </div>
      </MainLayout>
    );
  }

  const header = (
    <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center">
          <DollarOutlined className="text-emerald-600 dark:text-emerald-400 text-xl" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0">مشاهده معامله</h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm m-0">جزئیات کامل معامله #{id}</p>
        </div>
      </div>
      <Space size="small">
        <Button
          icon={<ArrowRightOutlined />}
          onClick={goBackToList}
          size="small"
          style={{ fontFamily: 'CustomFont' }}
        >
          بازگشت
        </Button>
        {canDeleteLeadRecord(permissions) && (
          <Popconfirm
            title="حذف معامله"
            description="آیا از حذف این معامله اطمینان دارید؟"
            onConfirm={handleDelete}
            okText="بله، حذف کن"
            cancelText="لغو"
            okButtonProps={{ danger: true }}
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              size="small"
              style={{ fontFamily: 'CustomFont' }}
            >
              حذف
            </Button>
          </Popconfirm>
        )}
      </Space>
    </div>
  );

  return (
    <MainLayout>
      <div className="" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
          <EntityBreadcrumb trail={trail} onNavigate={goToCrumb} />
          {header}

          {/* هدر چسبان — اطلاعات کلیدی معامله */}
          <div className="sticky top-0 z-20 px-4 sm:px-6 py-3 bg-white/95 dark:bg-gray-900/95 backdrop-blur-md border-b border-gray-200 dark:border-gray-800 shadow-sm">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-3 mb-3">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <Avatar
                  size={44}
                  src={resolveAvatarSrc(deal.image_url)}
                  icon={<UserOutlined />}
                  className="border border-white dark:border-gray-800 shadow-sm flex-shrink-0"
                  style={{ backgroundColor: '#52c41a' }}
                />
                <div className="min-w-0">
                  <h1 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-0.5 truncate">
                    {deal.name}
                  </h1>
                  <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                    {(customer?.name || lead?.client_name || lead?.name) && (
                      <span className="flex items-center gap-1 custom-font whitespace-nowrap">
                        <UserOutlined className="text-gray-400" />
                        {customer?.id ? (
                          <button
                            type="button"
                            onClick={() =>
                              navigateWithTrail(
                                `/clients/${customer.id}`,
                                customer.name || 'مشتری',
                                { returnTo: `/deals/${id}` },
                              )
                            }
                            className="text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 cursor-pointer custom-font"
                          >
                            {customer.name || lead?.client_name || lead?.name}
                          </button>
                        ) : (
                          <span>{customer?.name || lead?.client_name || lead?.name}</span>
                        )}
                      </span>
                    )}
                    {(customer?.mobile || lead?.mobile) && (
                      <span className="flex items-center gap-1 custom-font whitespace-nowrap">
                        <PhoneOutlined className="text-gray-400" />
                        {customer?.mobile || lead.mobile}
                        <CopyIconButton text={customer?.mobile || lead.mobile} />
                        <ClickToCallButton phone={customer?.mobile || lead.mobile} />
                        <SendSmsIconButton onClick={() => setSmsModalOpen(true)} />
                      </span>
                    )}
                    {customer?.email && (
                      <span className="flex items-center gap-1 custom-font whitespace-nowrap truncate max-w-[200px]">
                        {customer.email}
                      </span>
                    )}
                    {resolveCategoryName(deal) !== 'تعریف نشده' && (
                      <span className="flex items-center gap-1 custom-font whitespace-nowrap">
                        <TagOutlined className="text-gray-400" />
                        {resolveCategoryName(deal)}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="w-full lg:w-auto">
                {pipelineStages.length > 0 ? (
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                    <div className="flex-1 min-w-[200px]">
                      <span className="block text-xs text-gray-500 mb-1 custom-font">مرحله معامله</span>
                      <div className="relative group">
                        <div className="absolute -inset-0.5 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl blur opacity-20 group-hover:opacity-30 transition duration-300" />
                        <Select
                          value={deal.pipeline_stage_id || deal.stage_id ? Number(deal.pipeline_stage_id || deal.stage_id) : undefined}
                          onChange={handleStageChange}
                          loading={changingStage}
                          disabled={changingStage}
                          className="relative w-full bg-white dark:bg-gray-800 rounded-xl border-0 shadow-sm"
                          style={{ fontFamily: 'CustomFont' }}
                          size="middle"
                          placeholder="انتخاب مرحله"
                          suffixIcon={changingStage ? <Spin size="small" /> : <SwapOutlined className="text-emerald-500" />}
                          dropdownStyle={{ fontFamily: 'CustomFont' }}
                          optionLabelProp="label"
                          labelRender={({ value, label }) => {
                            const stage = pipelineStages.find((s) => Number(s.id) === Number(value));
                            const color = stage?.label_color || '#1890ff';
                            const name = stage?.name || label;
                            return (
                              <span className="flex items-center gap-2 min-w-0">
                                <span
                                  className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                                  style={{ backgroundColor: color }}
                                />
                                <span className="truncate">{name}</span>
                              </span>
                            );
                          }}
                        >
                          {pipelineStages.map((stage) => (
                            <Select.Option
                              key={stage.id}
                              value={stage.id}
                              label={stage.name}
                              className="py-2"
                            >
                              <div className="flex items-center gap-2">
                                <div
                                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                                  style={{ backgroundColor: stage.label_color || '#1890ff' }}
                                />
                                <span>{stage.name}</span>
                              </div>
                            </Select.Option>
                          ))}
                        </Select>
                      </div>
                    </div>
                  </div>
                ) : deal.stageName ? (
                  <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
                    <Tag color="blue" className="custom-font m-0">{deal.stageName}</Tag>
                  </div>
                ) : null}
              </div>
            </div>

            <EntityMiniStatCards
              items={[
                {
                  key: 'value',
                  label: 'ارزش معامله',
                  value: formatPriceToman(deal.value || 0),
                  show: deal.value > 0,
                  icon: <DollarOutlined className="text-green-600" />,
                  iconBgClass: 'bg-green-50 dark:bg-green-900/30',
                  borderClass: 'border-green-100 dark:border-green-900/40',
                },
                {
                  key: 'success_rate',
                  label: 'احتمال موفقیت',
                  value: `${deal.success_rate}%`,
                  show: Boolean(deal.success_rate),
                  icon: <CheckCircleOutlined className="text-blue-600" />,
                  iconBgClass: 'bg-blue-50 dark:bg-blue-900/30',
                  borderClass: 'border-blue-100 dark:border-blue-900/40',
                },
                {
                  key: 'priority',
                  label: 'اولویت',
                  value: <PriorityStars priority={deal.priority} size={14} />,
                  show: Boolean(deal.priority),
                  icon: <FlagOutlined style={{ color: getPriorityColor(deal.priority) }} />,
                  iconBgClass: 'bg-gray-50 dark:bg-gray-700',
                },
                {
                  key: 'close_date',
                  label: 'تاریخ بستن',
                  value: deal.close_date ? formatCloseDateLabel(deal.close_date) : '--',
                  show: Boolean(deal.close_date),
                  icon: <CalendarOutlined className="text-purple-600" />,
                  iconBgClass: 'bg-purple-50 dark:bg-purple-900/30',
                  borderClass: 'border-purple-100 dark:border-purple-900/40',
                },
                {
                  key: 'pipeline',
                  label: 'مسیر فروش',
                  value: deal.pipeline?.name || '—',
                  show: Boolean(deal.pipeline?.name),
                  icon: <PartitionOutlined className="text-indigo-600" />,
                  iconBgClass: 'bg-indigo-50 dark:bg-indigo-900/30',
                  borderClass: 'border-indigo-100 dark:border-indigo-900/40',
                },
                {
                  key: 'created_at',
                  label: 'تاریخ ایجاد معامله',
                  value: deal.created_at ? new Date(deal.created_at).toLocaleDateString('fa-IR') : '—',
                  show: Boolean(deal.created_at),
                  icon: <CalendarOutlined className="text-orange-600" />,
                  iconBgClass: 'bg-orange-50 dark:bg-orange-900/30',
                  borderClass: 'border-orange-100 dark:border-orange-900/40',
                },
              ]}
            />

            {deal.tags && deal.tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-3 pt-2 border-t border-gray-100 dark:border-gray-800">
                {deal.tags.map((tag) => (
                  <Tag key={tag.id} className="custom-font m-0 text-[11px] px-2 py-0" style={{ borderColor: tag.color, color: tag.color }}>
                    {tag.name}
                  </Tag>
                ))}
              </div>
            )}
          </div>

          <DealRelatedContactPanel
            customer={customer}
            lead={lead}
            navigate={navigate}
            onSendSms={() => setSmsModalOpen(true)}
            returnTo={`/deals/${id}`}
          />

          {/* بخش ویرایش فیلدها */}
          {canManageLeadRecord(permissions) && (
          <div className="px-4 sm:px-6 py-3 bg-gray-50/80 dark:bg-gray-900/40 border-b border-gray-200 dark:border-gray-800">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-0.5 h-4 bg-emerald-500 rounded-full" />
              <h3 className="text-sm font-medium text-gray-600 dark:text-gray-300 custom-font m-0">
                ویرایش سریع اطلاعات
              </h3>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
              {/* نام معامله */}
              <div ref={getFieldBoxRef('name')} className="group relative bg-white dark:bg-gray-800 px-2.5 py-2 rounded-md border border-gray-200 dark:border-gray-700 hover:border-emerald-300 dark:hover:border-emerald-600 transition-colors">
                <label className="block text-[10px] font-medium text-gray-500 dark:text-gray-400 mb-1 custom-font">
                  نام معامله
                </label>
                {editingField === 'name' ? (
                  <div className="flex items-center gap-1.5">
                    <Input
                      value={fieldValue || ''}
                      onChange={(e) => setFieldValue(e.target.value)}
                      onPressEnter={() => handleUpdateField('name', fieldValue)}
                      autoFocus
                      disabled={updatingField}
                      style={{ fontFamily: 'CustomFont', flex: 1 }}
                      size="small"
                      className="rounded-md"
                    />
                    <InlineEditActions
                      onSave={() => handleUpdateField('name', fieldValue)}
                      onCancel={cancelEditing}
                      saving={updatingField}
                    />
                  </div>
                ) : (
                  <div
                    className="flex items-center justify-between cursor-pointer"
                    onClick={() => startEditing('name', deal.name)}
                  >
                    <span className="text-sm text-gray-900 dark:text-gray-100 custom-font truncate flex-1">
                      {deal.name || 'تعریف نشده'}
                    </span>
                    <EditOutlined className="text-gray-400 group-hover:text-blue-500 transition-colors text-xs opacity-0 group-hover:opacity-100" />
                  </div>
                )}
              </div>

              {/* مسیر و مرحله فروش */}
              <div ref={getFieldBoxRef('pipeline')} className="group relative bg-white dark:bg-gray-800 px-2.5 py-2 rounded-md border border-gray-200 dark:border-gray-700 hover:border-emerald-300 dark:hover:border-emerald-600 transition-colors col-span-2 sm:col-span-2 lg:col-span-2">
                <label className="block text-[10px] font-medium text-gray-500 dark:text-gray-400 mb-1 custom-font">
                  <PartitionOutlined className="ml-1" />
                  مسیر و مرحله فروش
                </label>
                {editingField === 'pipeline' ? (
                  <div className="space-y-1.5">
                    <Select
                      value={fieldValue?.pipelineId}
                      onChange={(value) =>
                        setFieldValue((prev) => ({
                          ...prev,
                          pipelineId: value,
                          stageId: undefined,
                        }))
                      }
                      placeholder="انتخاب مسیر فروش"
                      style={{ width: '100%', fontFamily: 'CustomFont' }}
                      size="small"
                      disabled={changingStage}
                      showSearch
                      optionFilterProp="label"
                      options={pipelineOptions}
                    />
                    <Select
                      value={fieldValue?.stageId}
                      onChange={(value) =>
                        setFieldValue((prev) => ({ ...prev, stageId: value }))
                      }
                      placeholder={fieldValue?.pipelineId ? 'انتخاب مرحله' : 'ابتدا مسیر فروش را انتخاب کنید'}
                      style={{ width: '100%', fontFamily: 'CustomFont' }}
                      size="small"
                      disabled={changingStage || !fieldValue?.pipelineId}
                      showSearch
                      optionFilterProp="label"
                      options={pipelineEditStages.map((stage) => ({
                        value: stage.id,
                        label: stage.name,
                      }))}
                    />
                    <InlineEditActions
                      onSave={handleSavePipelineEdit}
                      onCancel={cancelEditing}
                      saving={changingStage}
                    />
                  </div>
                ) : (
                  <div
                    className="flex items-center justify-between cursor-pointer"
                    onClick={startEditingPipeline}
                  >
                    <span className="text-sm text-gray-900 dark:text-gray-100 custom-font truncate flex-1">
                      {deal.pipeline?.name || '—'}
                      {pipelineStages.length > 0 && (deal.pipeline_stage_id || deal.stage_id) ? (
                        <>
                          {' / '}
                          {pipelineStages.find(
                            (s) => Number(s.id) === Number(deal.pipeline_stage_id || deal.stage_id),
                          )?.name || '—'}
                        </>
                      ) : null}
                    </span>
                    <EditOutlined className="text-gray-400 group-hover:text-blue-500 transition-colors text-xs opacity-0 group-hover:opacity-100" />
                  </div>
                )}
              </div>

              {/* ارزش */}
              <div ref={getFieldBoxRef('value')} className="group relative bg-white dark:bg-gray-800 px-2.5 py-2 rounded-md border border-gray-200 dark:border-gray-700 hover:border-emerald-300 dark:hover:border-emerald-600 transition-colors">
                <label className="block text-[10px] font-medium text-gray-500 dark:text-gray-400 mb-1 custom-font">
                  ارزش (تومان)
                </label>
                {editingField === 'value' ? (
                  <div className="flex items-center gap-1.5">
                    <InputNumber
                      value={fieldValue || 0}
                      onChange={(value) => setFieldValue(value)}
                      onPressEnter={() => handleUpdateField('value', fieldValue)}
                      style={{ flex: 1, fontFamily: 'CustomFont' }}
                      formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                      min={0}
                      disabled={updatingField}
                      size="small"
                      className="rounded-md"
                    />
                    <InlineEditActions
                      onSave={() => handleUpdateField('value', fieldValue)}
                      onCancel={cancelEditing}
                      saving={updatingField}
                    />
                  </div>
                ) : (
                  <div
                    className="flex items-center justify-between cursor-pointer"
                    onClick={() => startEditing('value', deal.value || 0)}
                  >
                    <span className="text-sm text-gray-900 dark:text-gray-100 custom-font truncate flex-1">
                      {formatPriceToman(deal.value || 0)}
                    </span>
                    <EditOutlined className="text-gray-400 group-hover:text-blue-500 transition-colors text-xs opacity-0 group-hover:opacity-100" />
                  </div>
                )}
              </div>

              {/* احتمال موفقیت */}
              <div ref={getFieldBoxRef('success_rate')} className="group relative bg-white dark:bg-gray-800 px-2.5 py-2 rounded-md border border-gray-200 dark:border-gray-700 hover:border-emerald-300 dark:hover:border-emerald-600 transition-colors">
                <label className="block text-[10px] font-medium text-gray-500 dark:text-gray-400 mb-1 custom-font">
                احتمال موفقیت (%)
                </label>
                {editingField === 'success_rate' ? (
                  <div className="flex flex-col gap-1.5">
                    <Slider
                      min={0}
                      max={100}
                      step={25}
                      marks={SUCCESS_RATE_SLIDER_MARKS}
                      value={fieldValue ?? 0}
                      onChange={(value) => setFieldValue(value)}
                      disabled={updatingField}
                    />
                    <div className="flex items-center justify-between gap-1.5">
                      <span className="text-xs text-gray-500 custom-font">{fieldValue ?? 0}%</span>
                      <InlineEditActions
                        onSave={() => handleUpdateField('success_rate', fieldValue)}
                        onCancel={cancelEditing}
                        saving={updatingField}
                      />
                    </div>
                  </div>
                ) : (
                  <div
                    className="flex items-center justify-between cursor-pointer"
                    onClick={() => startEditing('success_rate', deal.success_rate || 0)}
                  >
                    <span className="text-sm text-gray-900 dark:text-gray-100 custom-font truncate flex-1">
                      {deal.success_rate ? `${deal.success_rate}%` : 'تعریف نشده'}
                    </span>
                    <EditOutlined className="text-gray-400 group-hover:text-blue-500 transition-colors text-xs opacity-0 group-hover:opacity-100" />
                  </div>
                )}
              </div>

              {false && (
              <>
              {/* دسته‌بندی معامله — فعلاً مخفی */}
              <div ref={getFieldBoxRef('category_id')} className="group relative bg-white dark:bg-gray-800 px-2.5 py-2 rounded-md border border-gray-200 dark:border-gray-700 hover:border-emerald-300 dark:hover:border-emerald-600 transition-colors">
                <label className="block text-[10px] font-medium text-gray-500 dark:text-gray-400 mb-1 custom-font">
                  دسته‌بندی معامله
                </label>
                {editingField === 'category_id' ? (
                  <div className="flex items-center gap-1.5">
                    <Select
                      value={fieldValue}
                      onChange={(value) => setFieldValue(value)}
                      style={{ flex: 1, fontFamily: 'CustomFont' }}
                      size="small"
                      disabled={updatingField}
                      showSearch
                      allowClear
                      placeholder="انتخاب دسته‌بندی"
                      className="rounded-md"
                      optionFilterProp="children"
                    >
                      {filters?.categories?.map((cat) => (
                        <Select.Option key={cat.id} value={cat.id}>
                          {cat.category_name || cat.name}
                        </Select.Option>
                      ))}
                    </Select>
                    <InlineEditActions
                      onSave={() => handleUpdateField('category_id', fieldValue)}
                      onCancel={cancelEditing}
                      saving={updatingField}
                    />
                  </div>
                ) : (
                  <div
                    className="flex items-center justify-between cursor-pointer"
                    onClick={() => startEditing('category_id', deal.category_id || deal.category?.id)}
                  >
                    <span className="text-sm text-gray-900 dark:text-gray-100 custom-font truncate flex-1">
                      {resolveCategoryName(deal)}
                    </span>
                    <EditOutlined className="text-gray-400 group-hover:text-blue-500 transition-colors text-xs opacity-0 group-hover:opacity-100" />
                  </div>
                )}
              </div>
              </>
              )}

              {/* شیوه آشنایی */}
              <div ref={getFieldBoxRef('source_id')} className="group relative bg-white dark:bg-gray-800 px-2.5 py-2 rounded-md border border-gray-200 dark:border-gray-700 hover:border-emerald-300 dark:hover:border-emerald-600 transition-colors">
                <label className="block text-[10px] font-medium text-gray-500 dark:text-gray-400 mb-1 custom-font">
                  شیوه آشنایی
                </label>
                {editingField === 'source_id' ? (
                  <div className="flex items-center gap-1.5">
                    <Select
                      value={fieldValue}
                      onChange={(value) => setFieldValue(value)}
                      style={{ flex: 1, fontFamily: 'CustomFont' }}
                      size="small"
                      disabled={updatingField}
                      showSearch
                      allowClear
                      placeholder="انتخاب شیوه آشنایی"
                      className="rounded-md"
                      optionFilterProp="children"
                    >
                      {(filters?.sources || []).map((source) => (
                        <Select.Option key={source.id} value={source.id}>
                          {source.type || source.name}
                        </Select.Option>
                      ))}
                    </Select>
                    <InlineEditActions
                      onSave={() => handleUpdateField('source_id', fieldValue)}
                      onCancel={cancelEditing}
                      saving={updatingField}
                    />
                  </div>
                ) : (
                  <div
                    className="flex items-center justify-between cursor-pointer"
                    onClick={() => startEditing('source_id', resolveSourceId(deal))}
                  >
                    <span className="text-sm text-gray-900 dark:text-gray-100 custom-font truncate flex-1">
                      {resolveSourceName(deal)}
                    </span>
                    <EditOutlined className="text-gray-400 group-hover:text-blue-500 transition-colors text-xs opacity-0 group-hover:opacity-100" />
                  </div>
                )}
              </div>

                {/* مالک معامله */}
                <div ref={getFieldBoxRef('deal_owner')} className="group relative bg-white dark:bg-gray-800 px-2.5 py-2 rounded-md border border-gray-200 dark:border-gray-700 hover:border-emerald-300 dark:hover:border-emerald-600 transition-colors">
                <label className="block text-[10px] font-medium text-gray-500 dark:text-gray-400 mb-1 custom-font">
                  <UserOutlined className="ml-1" />
                  مالک معامله
                </label>
                {editingField === 'deal_owner' ? (
                  <div className="flex items-center gap-1.5">
                    <Select
                      value={fieldValue}
                      onChange={(value) => setFieldValue(value)}
                      style={{ flex: 1, fontFamily: 'CustomFont' }}
                      size="small"
                      disabled={updatingField}
                      showSearch
                      allowClear
                      placeholder="انتخاب مالک"
                      className="rounded-md"
                      optionFilterProp="children"
                    >
                      {(filters?.dealWatcher || filters?.employees || []).map((emp) => (
                        <Select.Option key={emp.id} value={emp.id}>
                          {emp.name}
                        </Select.Option>
                      ))}
                    </Select>
                    <InlineEditActions
                      onSave={() => handleUpdateField('deal_owner', fieldValue)}
                      onCancel={cancelEditing}
                      saving={updatingField}
                    />
                  </div>
                ) : (
                  <div
                    className="flex items-center justify-between cursor-pointer"
                    onClick={() => startEditing('deal_owner', resolveDealOwnerId(deal))}
                  >
                    <span className="text-sm text-gray-900 dark:text-gray-100 custom-font truncate flex-1">
                      {resolveDealOwnerName(deal) || 'تعریف نشده'}
                    </span>
                    <EditOutlined className="text-gray-400 group-hover:text-blue-500 transition-colors text-xs opacity-0 group-hover:opacity-100" />
                  </div>
                )}
              </div>

              {/* اولویت */}
              <div ref={getFieldBoxRef('priority')} className="group relative bg-white dark:bg-gray-800 px-2.5 py-2 rounded-md border border-gray-200 dark:border-gray-700 hover:border-emerald-300 dark:hover:border-emerald-600 transition-colors">
                <label className="block text-[10px] font-medium text-gray-500 dark:text-gray-400 mb-1 custom-font">
                  اولویت
                </label>
                {editingField === 'priority' ? (
                  <div className="flex items-center gap-1.5">
                    <PriorityStars
                      value={fieldValue}
                      onChange={(v) => setFieldValue(v)}
                      size={22}
                      disabled={updatingField}
                    />
                    <InlineEditActions
                      onSave={() => handleUpdateField('priority', fieldValue)}
                      onCancel={cancelEditing}
                      saving={updatingField}
                    />
                  </div>
                ) : (
                  <div
                    className="flex items-center justify-between cursor-pointer"
                    onClick={() => startEditing('priority', deal.priority)}
                  >
                    <span className="text-sm text-gray-900 dark:text-gray-100 custom-font truncate flex-1">
                      {deal.priority ? (
                        <PriorityStars priority={deal.priority} size={18} />
                      ) : 'تعریف نشده'}
                    </span>
                    <EditOutlined className="text-gray-400 group-hover:text-blue-500 transition-colors text-xs opacity-0 group-hover:opacity-100" />
                  </div>
                )}
              </div>

              {/* تاریخ بستن */}
              <div ref={getFieldBoxRef('close_date')} className="group relative bg-white dark:bg-gray-800 px-2.5 py-2 rounded-md border border-gray-200 dark:border-gray-700 hover:border-emerald-300 dark:hover:border-emerald-600 transition-colors">
                <label className="block text-[10px] font-medium text-gray-500 dark:text-gray-400 mb-1 custom-font">
                  <CalendarOutlined className="ml-1" />
                  تاریخ بستن
                </label>
                {editingField === 'close_date' ? (
                  <div className="flex items-center gap-1.5">
                    <PersianDatePicker
                      value={fieldValue || null}
                      onChange={(val) => setFieldValue(val || null)}
                      placeholder="انتخاب تاریخ"
                      style={{ flex: 1, fontFamily: 'CustomFont' }}
                    />
                    <InlineEditActions
                      onSave={() => handleUpdateField('close_date', fieldValue)}
                      onCancel={cancelEditing}
                      saving={updatingField}
                    />
                  </div>
                ) : (
                  <div
                    className="flex items-center justify-between cursor-pointer"
                    onClick={() => startEditing('close_date', deal.close_date)}
                  >
                    <span className="text-sm text-gray-900 dark:text-gray-100 custom-font truncate flex-1">
                      {deal.close_date ? (
                        <>
                          <span>{formatCloseDateLabel(deal.close_date)}</span>
                          {formatCloseDateLabel(deal.close_date) !== toJalali(deal.close_date) && (
                            <span className="text-xs text-gray-400 mr-1">({toJalali(deal.close_date)})</span>
                          )}
                        </>
                      ) : 'تعریف نشده'}
                    </span>
                    <EditOutlined className="text-gray-400 group-hover:text-blue-500 transition-colors text-xs opacity-0 group-hover:opacity-100" />
                  </div>
                )}
              </div>


              {/* محصولات — زیر دسته‌بندی */}
              <div ref={getFieldBoxRef('product_id')} className="group relative bg-white dark:bg-gray-800 px-2.5 py-2 rounded-md border border-gray-200 dark:border-gray-700 hover:border-emerald-300 dark:hover:border-emerald-600 transition-colors col-span-2 sm:col-span-3 lg:col-span-4 xl:col-span-5">
                <label className="block text-[10px] font-medium text-gray-500 dark:text-gray-400 mb-1 custom-font">
                  <ShoppingOutlined className="ml-1" />
                  محصولات
                </label>
                {editingField === 'product_id' ? (
                  <div className="flex items-center gap-1.5">
                    <Select
                      mode="multiple"
                      value={fieldValue || []}
                      onChange={(value) => setFieldValue(value)}
                      style={{ flex: 1, fontFamily: 'CustomFont' }}
                      size="small"
                      disabled={updatingField}
                      showSearch
                      allowClear
                      placeholder="انتخاب محصولات"
                      className="rounded-md"
                      optionFilterProp="children"
                      maxTagCount="responsive"
                    >
                      {(filters?.products || []).map((product) => (
                        <Select.Option key={product.id} value={product.id}>
                          {product.name}
                        </Select.Option>
                      ))}
                    </Select>
                    <InlineEditActions
                      onSave={() => handleUpdateField('product_id', fieldValue)}
                      onCancel={cancelEditing}
                      saving={updatingField}
                    />
                  </div>
                ) : (
                  <div
                    className="flex items-start justify-between cursor-pointer min-h-[22px] gap-2"
                    onClick={() => startEditing('product_id', (deal.products || []).map(p => p.id))}
                  >
                    <div className="flex-1 min-w-0">
                      {deal.products && deal.products.length > 0 ? (
                        <p
                          className="text-sm text-gray-900 dark:text-gray-100 custom-font m-0 leading-5 line-clamp-2 break-words whitespace-normal"
                          title={deal.products.map((p) => p.name).join('، ')}
                        >
                          {deal.products.map((p) => p.name).join('، ')}
                        </p>
                      ) : (
                        <span className="text-sm text-gray-400 custom-font">بدون محصول</span>
                      )}
                    </div>
                    <EditOutlined className="text-gray-400 group-hover:text-blue-500 transition-colors text-xs opacity-0 group-hover:opacity-100 flex-shrink-0 mr-2 mt-0.5" />
                  </div>
                )}
              </div>

            
              {/* تگ‌ها — زیر مالک، تمام عرض */}
              <div ref={getFieldBoxRef('tags')} className="group relative bg-white dark:bg-gray-800 px-2.5 py-2 rounded-md border border-gray-200 dark:border-gray-700 hover:border-emerald-300 dark:hover:border-emerald-600 transition-colors col-span-2 sm:col-span-3 lg:col-span-4 xl:col-span-5">
                <label className="block text-[10px] font-medium text-gray-500 dark:text-gray-400 mb-1 custom-font">
                  <TagOutlined className="ml-1" />
                  تگ‌ها
                </label>
                {editingField === 'tags' ? (
                  <div className="flex items-center gap-1.5">
                    <Select
                      mode="multiple"
                      value={fieldValue || []}
                      onChange={(value) => setFieldValue(value)}
                      style={{ flex: 1, fontFamily: 'CustomFont' }}
                      size="small"
                      disabled={updatingField}
                      showSearch
                      allowClear
                      placeholder="انتخاب تگ‌ها"
                      className="rounded-md"
                      optionLabelProp="label"
                      optionFilterProp="label"
                      maxTagCount="responsive"
                    >
                      {tagOptions.map((tag) => (
                        <Select.Option key={tag.id} value={tag.id} label={tag.name}>
                          <span className="inline-flex items-center gap-2">
                            <span
                              className="inline-block w-2 h-2 rounded-full flex-shrink-0"
                              style={{ backgroundColor: tag.color || '#1890ff' }}
                            />
                            {tag.name}
                          </span>
                        </Select.Option>
                      ))}
                    </Select>
                    <InlineEditActions
                      onSave={() => handleUpdateField('tags', fieldValue)}
                      onCancel={cancelEditing}
                      saving={updatingField}
                    />
                  </div>
                ) : (
                  <div
                    className="flex items-center justify-between cursor-pointer min-h-[22px]"
                    onClick={() => startEditing('tags', (deal.tags || []).map(t => t.id))}
                  >
                    <div className="flex flex-wrap gap-1 flex-1">
                      {deal.tags && deal.tags.length > 0 ? (
                        deal.tags.map((tag) => (
                          <Tag
                            key={tag.id}
                            className="custom-font m-0 text-xs"
                            style={
                              tag.color
                                ? {
                                    borderColor: tag.color,
                                    color: tag.color,
                                    backgroundColor: `${tag.color}18`,
                                  }
                                : undefined
                            }
                          >
                            {tag.name}
                          </Tag>
                        ))
                      ) : (
                        <span className="text-sm text-gray-400 custom-font">بدون تگ</span>
                      )}
                    </div>
                    <EditOutlined className="text-gray-400 group-hover:text-blue-500 transition-colors text-xs opacity-0 group-hover:opacity-100 flex-shrink-0 mr-2" />
                  </div>
                )}
              </div>

              {/* توضیحات — تمام عرض */}
              <div ref={getFieldBoxRef('note')} className="group relative bg-white dark:bg-gray-800 px-2.5 py-2 rounded-md border border-gray-200 dark:border-gray-700 hover:border-emerald-300 dark:hover:border-emerald-600 transition-colors col-span-2 sm:col-span-3 lg:col-span-4 xl:col-span-5">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <label className="block text-[10px] font-medium text-gray-500 dark:text-gray-400 custom-font mb-0">
                    <FileTextOutlined className="ml-1" />
                    توضیحات
                  </label>
                  <Button
                    type="link"
                    size="small"
                    icon={<CopyOutlined />}
                    loading={copyingPrompt}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopyPrompt();
                    }}
                    className="p-0 h-auto text-violet-600 hover:!text-violet-700 dark:text-violet-300 text-[11px] custom-font"
                  >
                    کپی پرامپت سناریوی تماس
                  </Button>
                </div>
                {editingField === 'note' ? (
                  <div className="flex items-start gap-1.5">
                    <Input.TextArea
                      value={fieldValue || ''}
                      onChange={(e) => setFieldValue(e.target.value)}
                      autoFocus
                      disabled={updatingField}
                      style={{ fontFamily: 'CustomFont', flex: 1 }}
                      autoSize={{ minRows: 2, maxRows: 6 }}
                      maxLength={2000}
                      className="rounded-md"
                    />
                    <InlineEditActions
                      onSave={() => handleUpdateField('note', fieldValue)}
                      onCancel={cancelEditing}
                      saving={updatingField}
                    />
                  </div>
                ) : (
                  <div
                    className="flex items-start justify-between cursor-pointer min-h-[22px] gap-2"
                    onClick={() => startEditing('note', deal.note)}
                  >
                    <div className="flex-1 min-w-0">
                      {deal.note ? (
                        <ExpandableText
                          text={deal.note}
                          maxLines={3}
                          moreLabel="دیدن بیشتر"
                          lessLabel="کمتر"
                          textClassName="text-sm text-gray-900 dark:text-gray-100"
                        />
                      ) : (
                        <span className="text-sm text-gray-400 custom-font">بدون توضیحات</span>
                      )}
                    </div>
                    <EditOutlined className="text-gray-400 group-hover:text-blue-500 transition-colors text-xs opacity-0 group-hover:opacity-100 flex-shrink-0 mr-2 mt-0.5" />
                  </div>
                )}
              </div>
            </div>
          </div>
          )}

          {/* تب‌ها */}
          <Card 
            style={{ borderRadius: 18 }} 
            bodyStyle={{ padding: '4px 12px 16px' }} 
            className="sm:!px-4 sm:!pb-4"
          >
            <Tabs
              activeKey={activeTab}
              onChange={handleTabChange}
              destroyOnHidden
              size="middle"
              type="card"
              tabBarGutter={4}
              tabPosition="top"
              tabBarStyle={{
                marginBottom: 12,
                fontFamily: 'CustomFont',
                fontSize: 'clamp(11px, 2vw, 13px)',
                overflowX: 'auto',
                overflowY: 'hidden',
              }}
              style={{
                fontFamily: 'CustomFont',
              }}
              className="responsive-tabs"
              items={[
                {
                  key: 'follow-up',
                  label: (
                    <span style={{ fontFamily: 'CustomFont', fontSize: 'clamp(11px, 2vw, 13px)' }} className="flex items-center gap-1">
                      <CalendarOutlined style={{ fontSize: 'clamp(12px, 2vw, 14px)' }} />
                      <span className="hidden sm:inline">پیگیری‌ها</span>
                      <span className="sm:hidden">پیگیری</span>
                    </span>
                  ),
                  children: (
                    <Spin spinning={tabLoading}>
                      <FollowUpTab
                        dealId={id}
                        data={tabData}
                        filters={filters}
                        permissions={permissions}
                        onRefresh={() => fetchDealData('follow-up')}
                      />
                    </Spin>
                  ),
                },
                {
                  key: 'files',
                  label: (
                    <span style={{ fontFamily: 'CustomFont', fontSize: 'clamp(11px, 2vw, 13px)' }} className="flex items-center gap-1">
                      <FileOutlined style={{ fontSize: 'clamp(12px, 2vw, 14px)' }} />
                      <span className="hidden sm:inline">فایل‌ها</span>
                      <span className="sm:hidden">فایل</span>
                    </span>
                  ),
                  children: (
                    <Spin spinning={tabLoading}>
                      <FilesTab
                        dealId={id}
                        data={tabData}
                        filters={filters}
                        permissions={permissions}
                        onRefresh={() => fetchDealData('files')}
                      />
                    </Spin>
                  ),
                },
                {
                  key: 'notes',
                  label: (
                    <TabNotesLabel
                      count={notesCount}
                      icon={<FileTextOutlined style={{ fontSize: 'clamp(12px, 2vw, 14px)' }} />}
                    />
                  ),
                  children: (
                    <Spin spinning={tabLoading}>
                      <NotesTab
                        dealId={id}
                        data={tabData}
                        filters={filters}
                        permissions={permissions}
                        onRefresh={() => fetchDealData('notes')}
                      />
                    </Spin>
                  ),
                },
                {
                  key: 'history',
                  label: (
                    <span style={{ fontFamily: 'CustomFont', fontSize: 'clamp(11px, 2vw, 13px)' }} className="flex items-center gap-1">
                      <HistoryOutlined style={{ fontSize: 'clamp(12px, 2vw, 14px)' }} />
                      <span className="hidden sm:inline">تاریخچه</span>
                      <span className="sm:hidden">تاریخچه</span>
                    </span>
                  ),
                  children: (
                    <Spin spinning={tabLoading}>
                      <HistoryTab
                        dealId={id}
                        data={tabData}
                        filters={filters}
                        onRefresh={() => fetchDealData('history')}
                      />
                    </Spin>
                  ),
                },
                {
                  key: 'sms-history',
                  label: (
                    <span style={{ fontFamily: 'CustomFont', fontSize: 'clamp(11px, 2vw, 13px)' }} className="flex items-center gap-1">
                      <MessageOutlined style={{ fontSize: 'clamp(12px, 2vw, 14px)' }} />
                      <span className="hidden sm:inline">پیامک‌ها</span>
                      <span className="sm:hidden">پیامک</span>
                    </span>
                  ),
                  children: (
                    <SmsHistoryPanel
                      entityType="deal"
                      entityId={id}
                      recipientName={customer?.name || lead?.client_name || deal?.name}
                    />
                  ),
                },
                {
                  key: 'billing',
                  label: (
                    <span style={{ fontFamily: 'CustomFont', fontSize: 'clamp(11px, 2vw, 13px)' }} className="flex items-center gap-1">
                      <DollarOutlined style={{ fontSize: 'clamp(12px, 2vw, 14px)' }} />
                      <span className="hidden sm:inline">صورتحساب</span>
                      <span className="sm:hidden">صورتحساب</span>
                    </span>
                  ),
                  children: (
                    <Spin spinning={tabLoading}>
                      <BillingTab
                        dealId={id}
                        clientId={customer?.id || tabData?.invoices?.[0]?.client_id || tabData?.estimates?.[0]?.client_id}
                        data={tabData}
                        onRefresh={() => fetchDealData('billing')}
                        navigateWithTrail={navigateWithTrail}
                      />
                    </Spin>
                  ),
                },
              ]}
            />
          </Card>
        </Card>
      </div>

      <Modal
        title="دلیل ناموفق بودن معامله"
        open={rejectStageModal.open}
        onOk={handleConfirmRejectStage}
        onCancel={() =>
          setRejectStageModal({
            open: false,
            stageId: null,
            pipelineId: null,
            mode: 'stage',
            reasonId: null,
            reasonText: '',
          })
        }
        okText="ثبت دلیل و تغییر مرحله"
        cancelText="انصراف"
        confirmLoading={changingStage}
        okButtonProps={{ className: 'bg-red-600 hover:bg-red-700 border-0' }}
        destroyOnClose
      >
        <RejectReasonFields
          reasons={filters?.reject_reasons?.length ? filters.reject_reasons : rejectReasonsFallback}
          reasonId={rejectStageModal.reasonId}
          reasonText={rejectStageModal.reasonText}
          onReasonIdChange={(id) =>
            setRejectStageModal((prev) => ({ ...prev, reasonId: id, reasonText: '' }))
          }
          onReasonTextChange={(text) =>
            setRejectStageModal((prev) => ({ ...prev, reasonText: text }))
          }
          entityLabel="انتقال معامله به مرحله ناموفق"
        />
      </Modal>

      <SendSmsModal
        open={smsModalOpen}
        type="deal"
        recordId={id}
        recipientName={customer?.name || lead?.client_name || lead?.name || deal?.name}
        onClose={() => setSmsModalOpen(false)}
      />
    </MainLayout>
  );
};

export default ViewDeal;
